import { CellValue } from './types';

/**
 * Procedure run data, as a coded report sees it.
 *
 * A run is a procedure authored in veriform-web, executed on Veriform Desktop
 * and uploaded back. The app flattens it into a snapshot: the steps that were
 * run, in order, each with the readings its parameters hold. A report reads
 * the snapshot; it never touches the run tables itself.
 */

/** A run as a list of runs shows it. */
export interface RunSummary {
  id: string;
  title: string;
  /** PASSED / FAILED / PENDING / ABORTED / DEVIATED */
  status: string;
  /** CREATED / RUNNING / COMPLETED / INVALID */
  state: string;
  completedAt: string | null;
  signedBy: string | null;
  /** the web procedure or template the run was made from, when known */
  sourceTitle: string | null;
  /** the test report on this run, when it has one: which, and its latest published version */
  report?: RunReportSummary | null;
}

/** A run's own test report, as a list of runs shows it. */
export interface RunReportSummary {
  reportKey: string;
  title: string;
  /** the report template it was created from, when it was */
  reportTemplateId?: string | null;
  /** the newest published version; null while the report is only a working copy */
  latest: { number: number; verdict: 'passed' | 'failed' | 'incomplete'; verdictLabel: string; publishedAt: string } | null;
}

export type RunReadingKind = 'input' | 'output' | 'field' | 'other';

export type RunFileKind = 'image' | 'video' | 'pdf' | 'data' | 'other';

/** A file a reading holds — a photo, a scope capture, a points file. Rendered through the host by its id. */
export interface RunFile {
  fileId: string;
  fileName: string;
  kind: RunFileKind;
}

/** An instrument listed on the run. `configuration` holds whatever the equipment's object carries (manufacturer, model, serial…). */
export interface RunEquipment {
  name: string;
  identifier: string;
  description: string;
  category: string;
  configuration: Record<string, CellValue>;
}

/** One parameter of one step: its identifier and the value(s) it holds. */
export interface RunReading {
  parameterId: string;
  /** the template author's id for the parameter, e.g. `measure-the-maximum-inrush-current-for-region-1-oc1-t1` */
  identifier: string;
  title: string;
  kind: RunReadingKind;
  /** one per custom field; a list/table parameter has several */
  values: CellValue[];
  /** for object-typed fields: one record per field, keyed by object key */
  records: Array<Record<string, CellValue>>;
  /** file names, for file-typed fields */
  files: string[];
  /** the files themselves, for file-typed fields and files inside object fields; absent on snapshots taken before files were carried */
  attachments?: RunFile[];
  /** where the template author put it among its step's parameters (operation fields after them); absent on older snapshots */
  position?: number;
  /**
   * For a reading held by an operation, the operation's type: `File` is
   * reference material the template author attached for the operator (an
   * example diagram, an instruction photo); `FileInput`, `FieldInput`, `Field`
   * and `Table` are what the operator recorded. Absent for step parameters and
   * on older snapshots.
   */
  operationType?: string;
  /**
   * The reading this one takes its value from, where the procedure links
   * them — by its `parameterId`: the field an operator typed into and the
   * parameter it fills, a step input and the earlier output it reads. Absent
   * where nothing feeds it, and on older snapshots.
   */
  from?: string;
  /** For a choice parameter, every value it offers, in order; `values` holds the ones the run selected. Absent for any other parameter. */
  choices?: CellValue[];
}

/** A check a step makes over its readings, as the run recorded it: the template author's title, and the pass or fail message with the readings filled in. */
export interface RunValidation {
  title: string;
  passed: boolean;
  message: string;
}

export interface RunStep {
  id: string;
  title: string;
  /** section / scenario / repeating-step titles above this step, outermost first */
  path: string[];
  status: string;
  /** 0-based position among a repeating step's iterations; null for an ordinary step */
  iteration: number | null;
  /** the repeating step this iteration belongs to */
  iterationOf: string | null;
  readings: RunReading[];
  /** the step's checks, as recorded; absent on snapshots taken before validations were carried */
  validations?: RunValidation[];
}

export interface RunSnapshot extends RunSummary {
  projectId: string | null;
  /** the procedure template version the run was made from, when known */
  procedureTemplateId?: string | null;
  dut: { title: string; description?: string | null; partNumber: string | null; serial: string | null } | null;
  steps: RunStep[];
  /** the instruments the run lists; absent on snapshots taken before equipment was carried */
  equipment?: RunEquipment[];
  /** the procedure's description, as the run carries it */
  description?: string;
  /**
   * The procedure's own parameters, which the whole run shares: what it loops
   * over (temperatures, light functions — choices, with the ones this run
   * selected as its `values`), and lists it carries (an over-current envelope, region by
   * region). Absent on older snapshots.
   */
  parameters?: RunReading[];
}

/** A point file a run report wants read: one of the run's attachments, by id. */
export interface RunTraceRequest {
  /** what the report calls it; the trace comes back under this key in `context.traces` */
  key: string;
  fileId: string;
  /**
   * x ranges, in the file's own x units, to take the exact maximum of —
   * `[from, to)`. Decimation keeps every peak but not where a window edge
   * falls inside a point, so a maximum per window is taken from the full
   * samples and returned in `RunTrace.windowMax`, one per window.
   */
  windows?: Array<[number, number]>;
  /** points to reduce the file to, for a smaller chart; the server's default (and its most) when absent */
  points?: number;
}

/**
 * A point file as a report receives it: the first two numeric columns, x and
 * y, reduced to at most a couple of thousand points. Each point keeps the
 * minimum and maximum y of the samples it stands for, so a peak is never
 * averaged away — the envelope of the capture survives decimation exactly.
 */
export interface RunTrace {
  key: string;
  fileId: string;
  fileName: string;
  /** the header of each column, when the file has one */
  xLabel: string | null;
  yLabel: string | null;
  /** samples in the file */
  samples: number;
  /** [x, yMin, yMax], ascending x */
  points: Array<[number, number, number]>;
  /** the largest y of the full samples inside each requested window, in request order; null for a window no sample falls in */
  windowMax?: Array<number | null>;
  /** why the file could not be read; `points` is empty when set */
  error: string | null;
}

// ── helpers for report authors ──────────────────────────────────────────────

/**
 * Identifiers compare loosely: lower-case, every run of non-alphanumerics as
 * one underscore. That is how veriform-web derives an identifier from a title
 * (`formatStringForTemplating`), so `measure-the-maximum-inrush-current-for-region-1-oc1-t1`
 * and `measure_the_maximum_inrush_current_for_region_1_oc1_t1` are the same parameter.
 */
export function normalizeIdentifier(identifier: string): string {
  return identifier.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

export function sameIdentifier(a: string, b: string): boolean {
  return normalizeIdentifier(a) === normalizeIdentifier(b);
}

export function allReadings(run: RunSnapshot): RunReading[] {
  return run.steps.flatMap((s) => s.readings);
}

/** Every reading with this identifier, in step order. Identifiers repeat when a step repeats or a template reuses one. */
export function findReadings(run: RunSnapshot, identifier: string): RunReading[] {
  return allReadings(run).filter((r) => sameIdentifier(r.identifier, identifier));
}

export function findReading(run: RunSnapshot, identifier: string): RunReading | undefined {
  return findReadings(run, identifier)[0];
}

/** The step that holds a reading with this identifier. */
export function findStep(run: RunSnapshot, identifier: string): RunStep | undefined {
  return run.steps.find((s) => s.readings.some((r) => sameIdentifier(r.identifier, identifier)));
}

export function firstValue(reading: RunReading | undefined): CellValue {
  if (!reading) return null;
  const v = reading.values.find((x) => x !== null && x !== '');
  return v === undefined ? null : v;
}

export function readNumber(run: RunSnapshot, identifier: string): number | null {
  return toNumber(firstValue(findReading(run, identifier)));
}

export function readString(run: RunSnapshot, identifier: string): string | null {
  const v = firstValue(findReading(run, identifier));
  return v === null ? null : String(v);
}

/** A number from a reading in a particular step — for identifiers a template reuses across steps. */
export function stepNumber(step: RunStep | undefined, identifier: string): number | null {
  const reading = step?.readings.find((r) => sameIdentifier(r.identifier, identifier));
  return toNumber(firstValue(reading));
}

export function toNumber(v: CellValue): number | null {
  if (v === null || v === '') return null;
  const n = typeof v === 'number' ? v : Number(String(v).replace('−', '-'));
  return Number.isFinite(n) ? n : null;
}

/**
 * A number as a report prints it: six significant figures — more than any
 * bench instrument resolves — without a float's noise, so 2.3064821428571434
 * prints 2.30648 and 0.1 + 0.2 prints 0.3.
 */
export function formatNumber(n: number): string {
  return Number.isFinite(n) ? String(Number(n.toPrecision(6))) : String(n);
}

// a decimal with more places than an instrument gives, standing alone (not part of a version, date or id)
const NOISY_DECIMAL = /(?<![\w.])-?\d+\.\d{7,}(?![\w.])/g;

/** The numbers in a text a procedure wrote (a validation's message, a value stored as text) tidied the same way. */
export function tidyNumbers(text: string): string {
  return text.replace(NOISY_DECIMAL, (m) => formatNumber(Number(m)));
}

/** A recorded value as a report prints it. */
export function printValue(v: CellValue): string {
  if (v === null) return '';
  return typeof v === 'number' ? formatNumber(v) : tidyNumbers(String(v));
}

/** Case-insensitive search of a step's title and path for a word, e.g. the temperature corner a step ran at. */
export function stepMentions(step: RunStep | undefined, word: string): boolean {
  if (!step) return false;
  const needle = word.toLowerCase();
  return [step.title, ...step.path].some((t) => t.toLowerCase().includes(needle));
}

/** What a file is, from its name — how the snapshot sets `RunFile.kind`. */
export function fileKindOf(fileName: string): RunFileKind {
  if (/\.(jpe?g|png|gif|svg|webp|bmp)$/i.test(fileName)) return 'image';
  if (/\.(mp4|mov|webm|mkv)$/i.test(fileName)) return 'video';
  if (/\.pdf$/i.test(fileName)) return 'pdf';
  if (/\.(csv|tsv|txt|dat)$/i.test(fileName)) return 'data';
  return 'other';
}

/** One file on a run, with the step and reading that hold it. */
export interface RunAttachment {
  file: RunFile;
  step: RunStep;
  reading: RunReading;
}

/** Every file the run holds, in step order. */
export function allAttachments(run: RunSnapshot): RunAttachment[] {
  return run.steps.flatMap((step) => step.readings.flatMap((reading) => (reading.attachments ?? []).map((file) => ({ file, step, reading }))));
}

/** Files held by readings with this identifier, in step order. */
export function findAttachments(run: RunSnapshot, identifier: string): RunAttachment[] {
  return allAttachments(run).filter((a) => sameIdentifier(a.reading.identifier, identifier));
}

/**
 * The numbered region a step belongs to, e.g. 3 for `Measure the maximum inrush
 * current for "Region 3" (OC3 & t3)` — read from the step's title, then its path.
 */
export function stepRegion(step: RunStep | undefined): number | null {
  if (!step) return null;
  for (const text of [step.title, ...step.path.slice().reverse()]) {
    const match = /\bregion[\s_-]*(\d+)/i.exec(text);
    if (match) return Number(match[1]);
  }
  return null;
}

/** The largest y a trace reaches between two x values (inclusive); null when no point falls inside. */
export function traceMaxBetween(trace: RunTrace | undefined, x0: number, x1: number): number | null {
  if (!trace) return null;
  let max: number | null = null;
  for (const [x, , yMax] of trace.points) {
    if (x < x0 || x > x1) continue;
    if (max === null || yMax > max) max = yMax;
  }
  return max;
}
